import type { AccessManagementUserAuthContext } from '../models/access-management-auth.models';

export abstract class AccessManagementBaseAuthService {
  public authContext?: AccessManagementUserAuthContext;

  constructor() {
    // EDUS-1384: Firestore dependency moved to concrete implementations
  }

  // return the list of org units with only the org units that have proper permission
  public async getOrgUnitsWithPermission(
    customerId: number,
    userEmail: string,
    permissionCode: string,
    orgUnitsToCheck: string[]
  ): Promise<string[]> {
    await this.checkAuthContext(customerId, userEmail);
    if (!this.authContext) {
      return [];
    }

    const permissionedOrgUnitsSet =
      this.authContext.permissions.get(permissionCode);

    if (!permissionedOrgUnitsSet) {
      return [];
    }

    if (permissionedOrgUnitsSet.has('/')) {
      return orgUnitsToCheck;
    }

    const orgUnitsWithPermission = orgUnitsToCheck.filter((orgUnit) => {
      const parts = orgUnit.split('/');
      const orgUnitPathParts = parts
        .filter((part) => part)
        .map((_, i) => `/${parts.slice(1, i + 2).join('/')}`);

      return orgUnitPathParts.some((orgUnitPathPart: string): boolean => {
        return permissionedOrgUnitsSet.has(orgUnitPathPart);
      });
    });

    return orgUnitsWithPermission;
  }

  // check if permission exists on all org units in the list
  public async hasPermissionOnAllOrgUnits(
    customerId: number,
    userEmail: string,
    permissionCode: string,
    orgUnitsToCheck: string[]
  ): Promise<boolean> {
    return await this.hasPermission(
      customerId,
      userEmail,
      permissionCode,
      orgUnitsToCheck,
      true
    );
  }

  // check if permission exists on at least 1 org unit in the list
  public async hasPermissionOnAnyOrgUnit(
    customerId: number,
    userEmail: string,
    permissionCode: string,
    orgUnitsToCheck: string[]
  ): Promise<boolean> {
    return await this.hasPermission(
      customerId,
      userEmail,
      permissionCode,
      orgUnitsToCheck,
      false
    );
  }

  protected abstract loadEffectivePermissions(
    customerId: number,
    userEmail: string
  ): Promise<AccessManagementUserAuthContext>;

  private async checkAuthContext(
    customerId: number,
    userEmail: string
  ): Promise<void> {
    this.authContext = await this.loadEffectivePermissions(
      customerId,
      userEmail
    );
  }

  private async hasPermission(
    customerId: number,
    userEmail: string,
    permissionCode: string,
    orgUnitsToCheck: string[],
    checkAll = true
  ): Promise<boolean> {
    await this.checkAuthContext(customerId, userEmail);
    if (!this.authContext) {
      return false;
    }

    const permissionedOrgUnitsSet =
      this.authContext.permissions.get(permissionCode);

    if (!permissionedOrgUnitsSet) {
      return false;
    }

    if (permissionedOrgUnitsSet.has('/')) {
      return true;
    }

    if (!checkAll) {
      const hasAnyPermissions = orgUnitsToCheck.some((orgUnit) => {
        const parts = orgUnit.split('/');
        const orgUnitPathParts = parts
          .filter((part) => part)
          .map((_, i) => `/${parts.slice(1, i + 2).join('/')}`);

        return orgUnitPathParts.some((orgUnitPathPart: string): boolean => {
          return permissionedOrgUnitsSet.has(orgUnitPathPart);
        });
      });
      return hasAnyPermissions;
    }
    const hasAllPermissions = orgUnitsToCheck.every((orgUnit) => {
      const parts = orgUnit.split('/');
      const orgUnitPathParts = parts
        .filter((part) => part)
        .map((_, i) => `/${parts.slice(1, i + 2).join('/')}`);

      return orgUnitPathParts.some((orgUnitPathPart: string): boolean => {
        return permissionedOrgUnitsSet.has(orgUnitPathPart);
      });
    });
    return hasAllPermissions;
  }
}
